/**
 * Helpers for the "OCR could not read this file" path.
 *
 * When OCR fails outright the server still creates an EMPTY draft (statement or
 * invoice) and returns its id together with an informational `error` string.
 * That is NOT a fatal error: the user must be routed to the review page to
 * enter the data manually, instead of being stranded on the upload page.
 *
 * A second trap: after a browser VL-OCR fallback SUCCEEDS, the stale
 * `ocr_failed`/`error` flags from the primary attempt must be cleared or the
 * upload flow throws the old error even though the retry worked.
 */

export interface FailedDraftRoute {
  docType: 'bank_statement' | 'card_statement' | 'invoice';
  reviewId: string;
}

export function failedDraftRoute(result: any): FailedDraftRoute | null {
  if (!result || result.ocr_failed !== true) return null;
  if (typeof result.statement_id === 'string' && result.statement_id) {
    return {
      docType: result.type === 'card_statement' ? 'card_statement' : 'bank_statement',
      reviewId: result.statement_id,
    };
  }
  if (typeof result.invoice_id === 'string' && result.invoice_id) {
    return { docType: 'invoice', reviewId: result.invoice_id };
  }
  return null;
}

/**
 * Merge a successful browser VL-OCR fallback response into the primary result,
 * clearing the failure flags left over from the first, failed attempt.
 * Leaves the primary result untouched when the fallback itself failed.
 */
export function applyOcrFallback(result: any, vlResult: any): any {
  if (vlResult?.success) {
    Object.assign(result, vlResult);
    delete result.error;
    delete result.ocr_failed;
  }
  return result;
}
